import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./SafelightPaywall.module.scss"

/**
 * SafelightPaywall (`/safelight`) — an original, self-contained EXPIRATION /
 * renewal paywall for Private Photo Vault Premium. Paywall type rolled at random
 * for this run: EXPIRATION.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run was `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — told through a
 * language the security cluster has never used: a private DARKROOM. The one room
 * only you have the key to, where your memories live. Its SAFELIGHT is your
 * active protection.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * "The safelight went dark." Your term lapsed, so the darkroom is unlit — the
 * protection paused. But nothing is lost: every print still hangs on the wire.
 * Pull the safelight cord and the room blooms warm amber, your memories fade up
 * out of the dark, the door plate latches SECURED · ONLY YOU, and your real
 * safeguards light "on watch" one by one. Yearly keeps the light on for a whole
 * year; monthly is a small 30-day pull you come back to.
 *
 * ── Divergence (vs. the 90+ prior concepts, incl. the security cluster) ─────
 * Deliberately far from every security neighbour AND from the winding/renewal
 * concepts (Mainspring music-box wind, Ward crest-converge, Standby master-
 * switch, Hallmark seal, Vigil heartbeat, Beacon lighthouse-haul):
 * Layout      : a DARKROOM interior — a hanging safelight lamp, memory PRINTS
 *               pegged to a wire, an enamel door plate. Not a wall/crest/console.
 * Interaction : PULL THE CORD — a draggable, spring-loaded pull-chain with
 *               damped recoil. New to the whole set: not a switch-flip (Standby),
 *               not a key-wind (Mainspring), not a drag-slider (Static), not a
 *               press-hold (Ward/Decoy), not a tap-authenticate (Eyes Only).
 * Metaphor    : DARKROOM SAFELIGHT — the private room's protective light. Not a
 *               lock, vault, crest, seal, cipher, radar or clock.
 * Motion      : RE-ILLUMINATE — safelight bloom, prints fading up from black,
 *               warm glow spreading, door plate latching, guards lighting.
 * Feature     : real protections light "ON WATCH" as the safelight warms.
 * Value frame : PROTECTION-CONTINUITY / reinstatement — the light comes back on,
 *               warm not fearful; nothing was ever lost in the dark.
 * Register    : intimate sanctuary / calm relief — the hush of a private darkroom
 *               that is yours again.
 * CTA         : "Turn the safelight back on" — yearly = a full year lit, featured.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * Every safeguard named is a REAL Private Photo Vault feature (only-you lock with
 * Face ID / Touch ID / code · encrypted Cloud Vault · Decoy Vault · break-in
 * snapshots · app disguise). The expiration framing states plainly that NOTHING
 * is deleted — the prints still hang; only the extra protection paused — so there
 * is no data-loss threat. No countdown, no fabricated scarcity, no misleading
 * claim. "Save 79%" is real arithmetic from live product data. Yearly is the
 * featured, default, most prominent price; monthly is small and secondary. The
 * 7-day free trial is the real trial. Renders on built-in debug product data with
 * bundled photos — no native injection, no external egress.
 */

// The real Private Photo Vault safeguards — presented as guards that light
// "ON WATCH" as the safelight warms. `at` is the illumination level (0..1) at
// which each one comes on, so they cascade as the room brightens.
const GUARDS = [
  {
    key: "lock",
    Icon: svg.featureIcons.PatternLock,
    name: "Only-you lock",
    line: "Face ID, Touch ID or a code only you know",
    at: 0.16,
  },
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "a private, encrypted backup of every memory",
    at: 0.34,
  },
  {
    key: "decoy",
    Icon: svg.featureIcons.Decoy,
    name: "Decoy Vault",
    line: "a second code opens a harmless, empty vault",
    at: 0.52,
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-in snapshots",
    line: "a silent photo of anyone who tries a wrong code",
    at: 0.7,
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App disguise",
    line: "hide the whole vault behind an ordinary app",
    at: 0.86,
  },
]

// The prints pegged to the wire — bundled, self-contained memory photos reused
// from the repo. `x` is horizontal position along the wire (0..1); `drop` is how
// far the print hangs; `tilt` its resting angle; `at` when it fades up out of the
// dark as the safelight warms.
const PRINTS = [
  { src: "/safelight/m7.jpg", x: 0.11, drop: 8, tilt: -3.5, at: 0.1 },
  { src: "/safelight/m2.jpg", x: 0.35, drop: 20, tilt: 2.2, at: 0.24 },
  { src: "/safelight/m5.jpg", x: 0.6, drop: 6, tilt: -2.0, at: 0.42 },
  { src: "/safelight/m8.jpg", x: 0.84, drop: 22, tilt: 3.4, at: 0.6 },
  { src: "/safelight/m1.jpg", x: 0.24, drop: 74, tilt: 2.6, at: 0.3 },
  { src: "/safelight/m4.jpg", x: 0.49, drop: 86, tilt: -2.8, at: 0.5 },
  { src: "/safelight/m3.jpg", x: 0.73, drop: 72, tilt: 2.0, at: 0.72 },
]

// Pull distance (px) needed to relight the safelight.
const PULL_THRESHOLD = 58
const PULL_MAX = 96

function nextYearLabel() {
  const d = new Date()
  const target = new Date(d.getFullYear() + 1, d.getMonth(), 1)
  const month = target.toLocaleString("en-US", { month: "short" })
  return `${month} ${target.getFullYear()}`
}

const SafelightPaywall = () => {
  const { state, actions } = useAppState()
  const reduceMotion = useReducedMotion()

  // Match the safe-area insets to the darkroom so the scene runs edge to edge
  // instead of sitting inside pale status-bar bands.
  useEffect(() => {
    actions.setHeaderInsetColor("#0b0d11")
    actions.setFooterInsetColor("#070809")
  }, [actions])

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const throughLabel = useMemo(() => nextYearLabel(), [])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── illumination: 0 = dark (protection paused), 1 = fully lit (on watch) ────
  const [light, setLight] = useState(0)
  const lightRef = useRef(0)
  const raf = useRef<number | null>(null)
  const userActed = useRef(false)

  const setLightVal = useCallback((v: number) => {
    const c = Math.max(0, Math.min(1, v))
    lightRef.current = c
    setLight(c)
  }, [])

  const stopRaf = useCallback(() => {
    if (raf.current != null) cancelAnimationFrame(raf.current)
    raf.current = null
  }, [])

  const animateLightTo = useCallback(
    (target: number, durationMs: number) => {
      stopRaf()
      const from = lightRef.current
      const start = performance.now()
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / durationMs)
        const eased = t * t * (3 - 2 * t) // smootherstep
        setLightVal(from + (target - from) * eased)
        if (t < 1) raf.current = requestAnimationFrame(step)
        else raf.current = null
      }
      raf.current = requestAnimationFrame(step)
    },
    [stopRaf, setLightVal],
  )

  const lit = light >= 0.999

  // ── the pull-cord: a draggable pull-chain with damped spring recoil ─────────
  const [pull, setPull] = useState(0) // current cord displacement (px)
  const pullRef = useRef(0)
  const dragging = useRef(false)
  const startY = useRef(0)
  const startPull = useRef(0)
  const springRaf = useRef<number | null>(null)

  const setPullVal = useCallback((v: number) => {
    const c = Math.max(0, Math.min(PULL_MAX, v))
    pullRef.current = c
    setPull(c)
  }, [])

  // damped recoil: cord springs back to rest with a couple of small bounces
  const recoil = useCallback(() => {
    if (springRaf.current != null) cancelAnimationFrame(springRaf.current)
    let v = pullRef.current
    let vel = 0
    const k = 0.26 // stiffness
    const damp = 0.72 // damping
    const tick = () => {
      const force = -k * v
      vel = (vel + force) * damp
      v = v + vel
      if (Math.abs(v) < 0.4 && Math.abs(vel) < 0.4) {
        setPullVal(0)
        springRaf.current = null
        return
      }
      setPullVal(v)
      springRaf.current = requestAnimationFrame(tick)
    }
    springRaf.current = requestAnimationFrame(tick)
  }, [setPullVal])

  const relight = useCallback(() => {
    userActed.current = true
    if (reduceMotion) {
      setLightVal(1)
      return
    }
    animateLightTo(1, 1150)
  }, [reduceMotion, animateLightTo, setLightVal])

  const onCordDown = useCallback((e: React.PointerEvent) => {
    if (springRaf.current != null) cancelAnimationFrame(springRaf.current)
    dragging.current = true
    startY.current = e.clientY
    startPull.current = pullRef.current
    userActed.current = true
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
  }, [])

  const onCordMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return
      const delta = e.clientY - startY.current
      setPullVal(startPull.current + delta)
    },
    [setPullVal],
  )

  const onCordUp = useCallback(() => {
    if (!dragging.current) return
    dragging.current = false
    const pulled = pullRef.current
    recoil()
    if (pulled >= PULL_THRESHOLD && !lit) {
      relight()
    } else if (pulled >= PULL_THRESHOLD && lit) {
      // pull again while lit → let it rest (toggle off for a replay)
      animateLightTo(0, 620)
    }
  }, [recoil, relight, lit, animateLightTo])

  // Auto-demo one relight on mount so the payoff reads at a glance, then hand the
  // cord to the user. Respects reduced-motion (snaps lit immediately).
  useEffect(() => {
    if (reduceMotion) {
      setLightVal(1)
      return
    }
    const t = window.setTimeout(() => {
      if (!userActed.current) {
        // little tug on the cord, then the room warms
        setPullVal(PULL_THRESHOLD + 8)
        window.setTimeout(() => {
          if (!userActed.current) {
            recoil()
            animateLightTo(1, 1250)
          }
        }, 240)
      }
    }, 900)
    return () => {
      window.clearTimeout(t)
      stopRaf()
      if (springRaf.current != null) cancelAnimationFrame(springRaf.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const L = light
  const onCount = GUARDS.filter((g) => L >= g.at).length
  const cordExtra = pull + (lit ? 0 : 0)

  return (
    <div
      className={`${styles.root} ${lit ? styles.isLit : ""}`}
      style={{ ["--L" as any]: L }}
    >
      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.top}>
          <div className={styles.brand}>
            <span className={styles.brandMark} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="15" height="15">
                <path
                  d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 12.2l2.2 2.2L15 10.4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className={styles.brandName}>
              Private Photo Vault
              <span className={styles.brandTag}>Premium</span>
            </span>
          </div>
          <button
            className={styles.close}
            onClick={() => native.postNativeEvent("close")}
            aria-label="Close"
          >
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 6l12 12M18 6L6 18"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        {/* ── intro ──────────────────────────────────────────────────────── */}
        <section className={styles.intro}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true" />
            {lit ? "Darkroom · secured" : "Darkroom · protection paused"}
          </span>
          <h1 className={styles.title}>
            {lit ? (
              <>
                The safelight&rsquo;s <span className={styles.accent}>on.</span>
                <br />
                Your room is yours again.
              </>
            ) : (
              <>
                Your safelight <span className={styles.dim}>went dark.</span>
                <br />
                Every print still hangs.
              </>
            )}
          </h1>
        </section>

        {/* ── the darkroom (hero) ────────────────────────────────────────── */}
        <section className={styles.room} aria-hidden="true">
          {/* the safelight lamp + its light cone */}
          <div className={styles.lampWrap}>
            <div className={styles.cone} />
            <div className={styles.lamp}>
              <span className={styles.lampGlow} />
              <span className={styles.lampBody} />
              <span className={styles.lampFilament} />
            </div>
            {/* the pull cord — the interaction */}
            <div
              className={styles.cord}
              style={{ height: `${34 + cordExtra}px` }}
            >
              <span className={styles.cordLine} />
              <button
                className={styles.cordKnob}
                onPointerDown={onCordDown}
                onPointerMove={onCordMove}
                onPointerUp={onCordUp}
                onPointerCancel={onCordUp}
                aria-label={lit ? "Pull the cord" : "Pull the cord to relight"}
              >
                <span className={styles.cordBead} />
              </button>
            </div>
          </div>

          {/* the wire of memory prints */}
          <div className={styles.wireWrap}>
            <span className={styles.wire} />
            {PRINTS.map((p, i) => {
              const on = Math.max(0, Math.min(1, (L - p.at) / 0.22))
              return (
                <div
                  key={i}
                  className={styles.print}
                  style={{
                    left: `${p.x * 100}%`,
                    top: `${p.drop}px`,
                    ["--tilt" as any]: `${p.tilt}deg`,
                    ["--on" as any]: on,
                  }}
                >
                  <span className={styles.peg} />
                  <span
                    className={styles.photo}
                    style={{ backgroundImage: `url(${p.src})` }}
                  />
                  <span className={styles.photoTint} />
                  <span className={styles.photoGlow} />
                </div>
              )
            })}
          </div>

          {/* enamel door plate — latches SECURED when lit */}
          <div
            className={`${styles.plate} ${lit ? styles.plateOn : styles.plateOff}`}
          >
            <span className={styles.plateDot} />
            {lit ? "SECURED · ONLY YOU" : "UNLIT · PROTECTION PAUSED"}
          </div>

          {/* floating dust motes catching the light */}
          <div className={styles.motes}>
            {Array.from({ length: 7 }).map((_, i) => (
              <span key={i} className={styles.mote} data-m={i} />
            ))}
          </div>
        </section>

        {/* ── reassurance (honest expiration framing) ────────────────────── */}
        <p className={styles.assure}>
          <span className={styles.assureMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="13" height="13">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          Nothing was deleted in the dark — your photos are still locked safely
          on this device. Renewing simply turns your Premium protection back on.
        </p>

        {/* ── guards on watch (real safeguards) ──────────────────────────── */}
        <ul className={styles.guards}>
          <li className={styles.guardsHead}>
            <span>Back on watch</span>
            <span className={styles.guardsCount}>
              {onCount}/{GUARDS.length} lit
            </span>
          </li>
          {GUARDS.map((g) => {
            const on = L >= g.at
            return (
              <li
                key={g.key}
                className={`${styles.guard} ${on ? styles.guardOn : ""}`}
              >
                <span className={styles.guardIcon} aria-hidden="true">
                  <g.Icon width={16} height={16} />
                </span>
                <span className={styles.guardText}>
                  <span className={styles.guardName}>{g.name}</span>
                  <span className={styles.guardLine}>{g.line}</span>
                </span>
                <span className={styles.guardChip} aria-hidden="true">
                  {on ? "ON WATCH" : "•"}
                </span>
              </li>
            )
          })}
        </ul>

        {/* ── pricing: yearly featured, monthly small & secondary ────────── */}
        <div className={styles.plans}>
          <button
            className={`${styles.yearly} ${
              plan === "yearly" ? styles.selected : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            <span className={styles.yearlyGlow} aria-hidden="true" />
            <span className={styles.yearlyHead}>
              <span className={styles.yearlyName}>
                Keep the light on · 1 year
                <span className={styles.trialPill}>
                  {trialDays}-day free trial
                </span>
              </span>
              {savingsPct > 0 && (
                <span className={styles.saveTag}>Save {savingsPct}%</span>
              )}
            </span>
            <span className={styles.yearlyPriceRow}>
              <span className={styles.yearlyPrice}>{yearlyPrice}</span>
              <span className={styles.yearlyUnit}>/ year</span>
              <span className={styles.yearlyPerMo}>≈ {yearlyPerMonth}/mo</span>
              <span className={styles.check} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="13" height="13">
                  <path
                    d="M5 12.5l4.2 4.2L19 7"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </span>
            <span className={styles.yearlyThru}>
              Protection stays lit through {throughLabel}
            </span>
          </button>

          <button
            className={`${styles.monthly} ${
              plan === "monthly" ? styles.monthlySel : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span>Or a 30-day pull</span>
            <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaLabel}>
            {plan === "yearly"
              ? `Turn the safelight back on — ${trialDays}-day free trial`
              : "Turn the safelight back on"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `Then ${yearlyPrice}/year · cancel anytime`
              : `${monthlyPrice}/month · cancel anytime`}
          </span>
        </button>

        <div className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span className={styles.dot}>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default SafelightPaywall
